import React, { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Home, Layers, Mail, Moon, Sun } from "lucide-react";
import { Lockup } from "./brand/Mark";
import { cn } from "../lib/utils";

/**
 * One nav, two shapes: a bottom tab bar on phones — thumb reach, safe-area
 * aware — and the same bar floated to the top on desktop. Same items, same
 * order, so the mental model never changes between the two.
 */
const ITEMS = [
  { to: "/", label: "Start", icon: Home, end: true },
  { to: "/projects", label: "Werk", icon: Layers },
  { to: "/contact", label: "Contact", icon: Mail },
];

function readTheme() {
  try {
    const stored = localStorage.getItem("casper.theme");
    if (stored) return stored;
  } catch {
    /* private mode */
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export default function Navbar() {
  const [theme, setTheme] = useState(readTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem("casper.theme", theme);
    } catch {
      /* private mode */
    }
  }, [theme]);

  const toggle = () => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
    /* Tell the shell a human chose. The scroll-driven sunrise on the homepage
       has to stand down after this, or pressing "light" while at the top of the
       hero looks like a button that does nothing. */
    window.dispatchEvent(new Event("casper:theme-chosen"));
  };

  return (
    <>
      {/* ------------------------------------------------------- top bar */}
      <div
        className={cn(
          "fixed inset-x-0 top-0 z-40 flex items-center justify-between gap-2",
          "px-[15px] pt-[calc(10px+env(safe-area-inset-top))] pb-2.5",
          "desk:inset-x-auto desk:top-6 desk:right-6 desk:left-auto desk:px-0 desk:pt-0 desk:pb-0",
        )}
      >
        <Link to="/" aria-label="Naar start" className="tap desk:hidden">
          <Lockup size={21} />
        </Link>

        <button
          type="button"
          onClick={toggle}
          aria-label={theme === "dark" ? "Licht thema" : "Donker thema"}
          className="tap grid size-11 place-items-center rounded-full border border-edge bg-[var(--c-nav)] text-fg shadow-brand backdrop-blur-[28px]"
        >
          {theme === "dark" ? (
            <Sun aria-hidden className="size-4" strokeWidth={1.75} />
          ) : (
            <Moon aria-hidden className="size-4" strokeWidth={1.75} />
          )}
        </button>
      </div>

      {/* ------------------------------------------------------- the nav */}
      <nav
        aria-label="Hoofdnavigatie"
        className={cn(
          "fixed left-1/2 z-50 -translate-x-1/2 rounded-[24px] border p-1.5",
          "backdrop-blur-[28px] backdrop-saturate-[180%]",
          "bottom-[calc(14px+env(safe-area-inset-bottom))] w-[calc(100%-24px)] max-w-[420px]",
          "desk:top-6 desk:bottom-auto desk:w-auto desk:max-w-none desk:rounded-full desk:p-[5px]",
        )}
        style={{
          background: "var(--c-nav)",
          borderColor: "var(--c-edge)",
          boxShadow: "var(--c-shadow)",
        }}
      >
        <ul className="flex gap-px desk:gap-1">
          {ITEMS.map(({ to, label, icon: Icon, end }) => (
            <li key={to} className="flex-1 desk:flex-none">
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    "tap relative flex flex-col items-center gap-[5px] rounded-[18px] px-0.5 py-[9px]",
                    "text-[9px] font-semibold tracking-[0.02em] transition-colors duration-200",
                    "desk:flex-row desk:gap-2 desk:rounded-full desk:px-[18px] desk:py-[11px] desk:text-[12.5px]",
                    isActive ? "bg-fg text-bg" : "text-muted hover:text-fg",
                  )
                }
              >
                <Icon aria-hidden className="size-[18px] desk:size-[15px]" strokeWidth={1.5} />
                <span>{label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
}
